import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { metaReducers } from './meta-reducers';
import { authInterceptor } from './interceptors/auth.interceptor';
import { errorInterceptor } from './interceptors/error.interceptor';
import { LanguageService } from './services/language.service';
import { ThemeService } from './services/theme.service';

import { areasFeatureKey, reducer as areasReducer } from './state/areas/areas.reducer';
import { AreasEffects } from './state/areas/areas.effects';

import { complaintTypesFeatureKey, reducer as complaintTypesReducer } from './state/complaint-types/complaint-types.reducer';
import { ComplaintTypesEffects } from './state/complaint-types/complaint-types.effects';

import { modelsFeatureKey, reducer as modelsReducer } from './state/models/models.reducer';
import { ModelsEffects } from './state/models/models.effects';

import { refrigerantsFeatureKey, reducer as refrigerantsReducer } from './state/refrigerants/refrigerants.reducer';
import { RefrigerantsEffects } from './state/refrigerants/refrigerants.effects';

import { usualFeedbacksFeatureKey, reducer as usualFeedbacksReducer } from './state/usual-feedbacks/usual-feedbacks.reducer';
import { UsualFeedbacksEffects } from './state/usual-feedbacks/usual-feedbacks.effects';

import { authFeatureKey, reducer as authReducer } from './state/auth/auth.reducer';
import { AuthEffects } from './state/auth/auth.effects';

import { personnelFeatureKey, reducer as personnelReducer } from './state/personnel/personnel.reducer';
import { PersonnelEffects } from './state/personnel/personnel.effects';

import { rolesFeatureKey, reducer as rolesReducer } from './state/roles/roles.reducer';
import { RolesEffects } from './state/roles/roles.effects';

import { customersFeatureKey, reducer as customersReducer } from './state/customers/customers.reducer';
import { CustomersEffects } from './state/customers/customers.effects';

import { partnersFeatureKey, reducer as partnersReducer } from './state/partners/partners.reducer';
import { PartnersEffects } from './state/partners/partners.effects';

import { fleetFeatureKey, reducer as fleetReducer } from './state/fleet/fleet.reducer';
import { FleetEffects } from './state/fleet/fleet.effects';

import { goodsFeatureKey, reducer as goodsReducer } from './state/goods/goods.reducer';
import { GoodsEffects } from './state/goods/goods.effects';

import { sparePartsFeatureKey, reducer as sparePartsReducer } from './state/spare-parts/spare-parts.reducer';
import { SparePartsEffects } from './state/spare-parts/spare-parts.effects';

import { cylindersFeatureKey, reducer as cylindersReducer } from './state/cylinders/cylinders.reducer';
import { CylindersEffects } from './state/cylinders/cylinders.effects';

// Phase 9 — Devices, Orders, Work Orders, Visits
import { devicesFeatureKey, reducer as devicesReducer } from './state/devices/devices.reducer';
import { DevicesEffects } from './state/devices/devices.effects';

import { ordersFeatureKey, reducer as ordersReducer } from './state/orders/orders.reducer';
import { OrdersEffects } from './state/orders/orders.effects';

import { visitsFeatureKey, reducer as visitsReducer } from './state/visits/visits.reducer';
import { VisitsEffects } from './state/visits/visits.effects';

import { workOrdersFeatureKey, reducer as workOrdersReducer } from './state/work-orders/work-orders.reducer';
import { WorkOrdersEffects } from './state/work-orders/work-orders.effects';

// Phase 10 — Finance
import { salesTransactionsFeatureKey, reducer as salesTransactionsReducer } from './state/sales-transactions/sales-transactions.reducer';
import { SalesTransactionsEffects } from './state/sales-transactions/sales-transactions.effects';

import { cashPositionsFeatureKey, reducer as cashPositionsReducer } from './state/cash-positions/cash-positions.reducer';
import { CashPositionsEffects } from './state/cash-positions/cash-positions.effects';

import { transfersFeatureKey, reducer as transfersReducer } from './state/transfers/transfers.reducer';
import { TransfersEffects } from './state/transfers/transfers.effects';

import { spendingFeatureKey, reducer as spendingReducer } from './state/spending/spending.reducer';
import { SpendingEffects } from './state/spending/spending.effects';

import { invoicesFeatureKey, reducer as invoicesReducer } from './state/invoices/invoices.reducer';
import { InvoicesEffects } from './state/invoices/invoices.effects';

import { warehousesFeatureKey, reducer as warehousesReducer } from './state/warehouses/warehouses.reducer';
import { WarehousesEffects } from './state/warehouses/warehouses.effects';

import { customFieldsFeatureKey, reducer as customFieldsReducer } from './state/custom-fields/custom-fields.reducer';
import { CustomFieldsEffects } from './state/custom-fields/custom-fields.effects';

import { customFieldGroupsFeatureKey, customFieldGroupsReducer } from './state/custom-fields/custom-field-groups.reducer';
import { CustomFieldGroupsEffects } from './state/custom-fields/custom-field-groups.effects';

// Multi-tenancy migration plan, Phase 3 — Super Admin tenant management
import { tenantsFeatureKey, reducer as tenantsReducer } from './state/tenants/tenants.reducer';
import { TenantsEffects } from './state/tenants/tenants.effects';

export function initializeApp(languageService: LanguageService, themeService: ThemeService) {
  return () => {
    languageService.init();
    themeService.init();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideStore({}, { metaReducers }),

    // Phase 4 — Lookups
    provideState(areasFeatureKey, areasReducer),
    provideState(complaintTypesFeatureKey, complaintTypesReducer),
    provideState(refrigerantsFeatureKey, refrigerantsReducer),
    provideState(modelsFeatureKey, modelsReducer),
    provideState(usualFeedbacksFeatureKey, usualFeedbacksReducer),

    // Phase 5 — Identity
    provideState(authFeatureKey, authReducer),
    provideState(personnelFeatureKey, personnelReducer),
    provideState(rolesFeatureKey, rolesReducer),

    // Phase 6 — Customers & Partners
    provideState(customersFeatureKey, customersReducer),
    provideState(partnersFeatureKey, partnersReducer),

    // Phase 7 — Fleet
    provideState(fleetFeatureKey, fleetReducer),

    // Phase 8 — Inventory
    provideState(goodsFeatureKey, goodsReducer),
    provideState(sparePartsFeatureKey, sparePartsReducer),
    provideState(cylindersFeatureKey, cylindersReducer),

    // Phase 9 — Devices, Orders, Work Orders, Visits
    provideState(devicesFeatureKey, devicesReducer),
    provideState(ordersFeatureKey, ordersReducer),
    provideState(visitsFeatureKey, visitsReducer),
    provideState(workOrdersFeatureKey, workOrdersReducer),

    // Warehouses
    provideState(warehousesFeatureKey, warehousesReducer),

    // Custom Fields
    provideState(customFieldsFeatureKey, customFieldsReducer),
    provideState(customFieldGroupsFeatureKey, customFieldGroupsReducer),

    // Multi-tenancy migration plan, Phase 3
    provideState(tenantsFeatureKey, tenantsReducer),

    // Phase 10 — Finance
    provideState(salesTransactionsFeatureKey, salesTransactionsReducer),
    provideState(cashPositionsFeatureKey, cashPositionsReducer),
    provideState(transfersFeatureKey, transfersReducer),
    provideState(spendingFeatureKey, spendingReducer),
    provideState(invoicesFeatureKey, invoicesReducer),

    provideEffects([
      AreasEffects, ComplaintTypesEffects, RefrigerantsEffects, ModelsEffects, UsualFeedbacksEffects,
      AuthEffects, PersonnelEffects, RolesEffects,
      CustomersEffects, PartnersEffects,
      FleetEffects,
      GoodsEffects, SparePartsEffects, CylindersEffects,
      DevicesEffects, OrdersEffects, VisitsEffects, WorkOrdersEffects,
      SalesTransactionsEffects, CashPositionsEffects, TransfersEffects, SpendingEffects, InvoicesEffects,
      WarehousesEffects,
      CustomFieldsEffects,
      CustomFieldGroupsEffects,
      TenantsEffects
    ]),

    provideTranslateService({ defaultLanguage: 'ar' }),
    provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: '.json' }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [LanguageService, ThemeService],
      multi: true
    }
  ]
};
